import React, { useState } from 'react';
import { HiOutlineArrowRight, HiOutlineArrowPath } from 'react-icons/hi2';
import cloudApi from '../../../services/cloudApi';
import analytics from '../../../services/analytics';
import ClaudeMark from '../../../components/ClaudeMark';
import { isClaudeGrant } from '../../../utils/mcpClients';
import useOAuthGrants from '../../../hooks/useOAuthGrants';
import ConnectedClientRow from './ConnectedClientRow';
import { CardBodySkeleton } from './CardSkeleton';
import ConnectClaudeModal from '../../ConnectClaude/ConnectClaudeModal';

// The "Claude" integration card — the single place, for every user, to connect
// and disconnect Claude. Connecting is sign-in based (handled by the shared
// ConnectClaudeModal, the same wizard /connect-claude uses); "connected apps" are
// the OAuth grants Claude created when someone approved access, and disconnecting
// revokes that grant immediately. Every other MCP client connects through the
// sibling cards: ChatGPT has its own, and the MCP card owns the token clients.
// This card lists Claude's grants only — any OAuth client can reach the same
// endpoint, and listing those here would file them under Anthropic's mark.

const ClaudeIntegrationCard = () => {
  const { grants, loading, reload, removeGrant } = useOAuthGrants(isClaudeGrant);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [disconnecting, setDisconnecting] = useState(null); // grant id
  const [modalOpen, setModalOpen] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    await reload();
    setRefreshing(false);
  };

  const handleDisconnect = async (grantId) => {
    setDisconnecting(grantId);
    setError(null);
    try {
      const grant = grants.find((g) => g.id === grantId);
      await cloudApi.revokeOAuthGrant(grantId);
      analytics.trackClaudeDisconnected({
        source: 'integrations',
        scopes: grant?.scopes,
        client_host: grant?.clientHost,
      });
      removeGrant(grantId);
    } catch (err) {
      setError(err.message || 'Failed to disconnect.');
    } finally {
      setDisconnecting(null);
    }
  };

  const openModal = () => {
    analytics.trackClaudeConnectStarted({
      source: 'integrations',
      already_connected: grants.length > 0,
    });
    setModalOpen(true);
  };

  // Connecting happens inside Claude's UI, so re-check grants when the wizard
  // closes — a freshly approved connection then shows up right away.
  const closeModal = () => {
    setModalOpen(false);
    reload();
  };

  const isConnected = grants.length > 0;

  return (
    <div className="bg-white/60 dark:bg-gray-900/40 backdrop-blur-xl rounded-xl border border-gray-200/60 dark:border-gray-700/40 shadow-lg shadow-gray-200/40 dark:shadow-black/20 p-6 flex flex-col transition-all duration-300 hover:shadow-xl">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-4">
          {/* Claude's mark is already a finished app-tile, so it fills the icon
              footprint edge-to-edge — no inner background/padding boxing a tile
              inside a tile. Same size + ring as the sibling cards' icon tiles so
              it lines up. Shown unmodified per Anthropic's trademark guidelines. */}
          <div className="flex-shrink-0 w-12 h-12 rounded-xl overflow-hidden shadow-md shadow-gray-200/30 dark:shadow-black/20 ring-1 ring-gray-200/80 dark:ring-gray-700/60">
            <ClaudeMark className="w-full h-full" rounded="rounded-none" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">Claude</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Scrape, query &amp; run automations by asking</p>
          </div>
        </div>

        {isConnected && (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
            Connected
          </span>
        )}
      </div>

      {loading ? (
        <CardBodySkeleton />
      ) : isConnected ? (
        <div className="flex-1 flex flex-col border-t border-gray-200 dark:border-gray-700/30 pt-4">
          <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-3">
            Claude can query your tables, clean up columns, run automations and launch new extractions —
            just by asking. Disconnecting revokes access immediately.
          </p>

          {error && (
            <div className="px-3 py-2 mb-3 rounded-lg text-sm bg-red-50/90 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200/60 dark:border-red-700/30">
              {error}
            </div>
          )}

          <div className="rounded-xl bg-gray-50/80 dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.06] divide-y divide-gray-100 dark:divide-white/[0.04]">
            {grants.map((grant) => (
              <ConnectedClientRow
                key={grant.id}
                grant={grant}
                onDisconnect={handleDisconnect}
                disconnecting={disconnecting === grant.id}
              />
            ))}
          </div>

          <div className="flex items-center justify-end gap-2 mt-auto pt-3">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800/50 rounded-lg transition-all duration-200 disabled:opacity-50"
            >
              <HiOutlineArrowPath className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={openModal}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-800 dark:bg-white/[0.1] text-white dark:text-gray-100 font-medium text-sm rounded-xl border border-gray-700 dark:border-white/[0.08] hover:bg-gray-700 dark:hover:bg-white/[0.15] transition-all duration-200"
            >
              Connect another
              <HiOutlineArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col border-t border-gray-200 dark:border-gray-700/30 pt-4">
          <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-4">
            Turn Claude into a web scraper: launch extractions, get clean tables back, then filter and tidy
            them — just by asking. No API keys; disconnect anytime.
          </p>

          {error && (
            <div className="px-3 py-2 mb-3 rounded-lg text-sm bg-red-50/90 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200/60 dark:border-red-700/30">
              {error}
            </div>
          )}

          <div className="mt-auto">
            <button
              onClick={openModal}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-800 dark:bg-white/[0.1] text-white dark:text-gray-100 font-medium text-sm rounded-xl border border-gray-700 dark:border-white/[0.08] hover:bg-gray-700 dark:hover:bg-white/[0.15] transition-all duration-200"
            >
              Connect Claude
              <HiOutlineArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <ConnectClaudeModal isOpen={modalOpen} onClose={closeModal} source="integrations" />
    </div>
  );
};

export default ClaudeIntegrationCard;
